
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useCandidateActions } from '@/hooks/useCandidateActions';
import { Phone } from 'lucide-react';

interface CallDialogProps {
  isOpen: boolean;
  onClose: () => void;
  candidateId: string;
  candidateName: string;
  candidatePhone?: string;
}

export const CallDialog: React.FC<CallDialogProps> = ({
  isOpen,
  onClose,
  candidateId,
  candidateName,
  candidatePhone,
}) => {
  const [phoneNumber, setPhoneNumber] = useState(candidatePhone || '');
  const [notes, setNotes] = useState('');
  const { initiateCall, isProcessing } = useCandidateActions();

  const handleCall = async () => {
    if (!phoneNumber.trim()) return;
    
    const success = await initiateCall(candidateId, phoneNumber);
    
    if (success) {
      setNotes('');
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5" />
            Call {candidateName}
          </DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="phone">Phone Number:</Label>
            <Input
              id="phone"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number..."
              type="tel"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="notes">Call Notes (Optional):</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this call..."
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleCall} 
            disabled={isProcessing || !phoneNumber.trim()}
          >
            {isProcessing ? 'Calling...' : 'Start Call'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
